import { formatInTimeZone } from 'date-fns-tz';
import { MarketplaceLogo } from 'src/components/common/MarketplaceLogo';
import { MarketplacePaymentStatePill } from 'src/components/common/MarketplacePaymentStatePill/MarketplacePaymentStatePill';
import { Content, useContent } from 'src/contexts/ContentContext';
import { useSiteTimezoneContext } from 'src/contexts/SiteTimezoneContext/SiteTimezoneContext';
import { DEFAULT_TIMEZONE_LOCAL } from 'src/contexts/SiteTimezoneContext/SiteTimezoneContext.constants';
import { vars } from 'src/core/themes';
import { Stack } from 'src/core/ui';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { LockIcon } from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';
import { MARKETPLACE_PAYMENT_TERM_TO_CID } from 'src/utils/constants/contentIdMaps';
import { Feature, MarketplaceSalePayment } from 'src/WebApiController';

import * as styles from './MarketplacePaymentHeaderDetails.css';
import { MarketplacePaymentsDetailsFilter } from './MarketplacePaymentsDetailsFilter';

interface MarketplacePaymentHeaderDetailsProps {
  marketplaceSalePayment: MarketplaceSalePayment;
}

export const MarketplacePaymentHeaderDetails = ({
  marketplaceSalePayment,
}: MarketplacePaymentHeaderDetailsProps) => {
  const { timeZone } = useSiteTimezoneContext();
  const lockedText = useContent(ContentId.Locked);

  const hasMarketplacePaymentLineFilterFeature = useUserHasFeature(
    Feature.MarketplacePaymentLineFilter
  );

  return (
    <Stack
      justifyContent="spaceBetween"
      width="full"
      className={styles.wrapper}
    >
      <Stack direction="column" gap="m">
        <div>
          <span className={styles.title}>
            <Content id={ContentId.PaymentId} />:
          </span>
          <span>{marketplaceSalePayment.extPmtId}</span>
        </div>
        {marketplaceSalePayment.pmtDate && (
          <div>
            <span className={styles.title}>
              <Content id={ContentId.PaymentDate} />:
            </span>
            <span>
              {formatInTimeZone(
                new Date(marketplaceSalePayment.pmtDate),
                timeZone ?? DEFAULT_TIMEZONE_LOCAL,
                'do LLL yyyy'
              )}
            </span>
          </div>
        )}
        {marketplaceSalePayment.mkpPmtTerm && (
          <div>
            <span className={styles.title}>
              <Content id={ContentId.MarketplacePaymentTerm} />:
            </span>
            <span>
              <Content
                id={
                  MARKETPLACE_PAYMENT_TERM_TO_CID[
                    marketplaceSalePayment.mkpPmtTerm
                  ]
                }
              />
            </span>
          </div>
        )}
      </Stack>
      <Stack direction="column" gap="m" alignItems="center">
        <Stack alignItems="center" gap="m" alignSelf="start">
          {marketplaceSalePayment.isLocked && (
            <LockIcon
              size={vars.iconSize.m}
              fill={vars.color.textBrand}
              withHoverEffect
              title={lockedText}
            />
          )}
          <MarketplacePaymentStatePill state={marketplaceSalePayment.state} />
          <MarketplaceLogo marketplace={marketplaceSalePayment.mkp} />
        </Stack>
        {hasMarketplacePaymentLineFilterFeature && (
          <MarketplacePaymentsDetailsFilter />
        )}
      </Stack>
    </Stack>
  );
};
